<!-- eslint-disable prefer-destructuring -->
<template>
  <div class="budgets-page">
    <div class="content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-top">
            <button class="btn back-btn" @click="goBack">
              <i class="fas fa-arrow-left"></i>
              Back to Dashboard
            </button>
          </div>
          <h1 class="cabin-family bold">Budget Management</h1>
          <p class="secondary-text">Track your spending and stay on budget</p>
        </div>
        <div class="header-actions">
          <button
            class="btn primary-btn"
            @click="openCreateBudgetModal"
          >
            <i class="fas fa-plus"></i>
            Create Budget
          </button>
        </div>
      </div>

      <!-- Budget Overview Cards -->
      <div class="overview-cards">
        <div class="overview-card">
          <div class="card-icon primary">
            <i class="fas fa-calculator"></i>
          </div>
          <div class="card-content">
            <h3 class="cabin-family semi-bold">{{ activeBudgets.length }}</h3>
            <p class="secondary-text">Active Budgets</p>
          </div>
        </div>

        <div class="overview-card">
          <div class="card-icon success">
            <i class="fas fa-wallet"></i>
          </div>
          <div class="card-content">
            <h3 class="cabin-family semi-bold">{{ formatCurrency(totalBudgetAmount) }}</h3>
            <p class="secondary-text">Total Budget</p>
          </div>
        </div>

        <div class="overview-card">
          <div class="card-icon" :class="getSpentColorClass()">
            <i class="fas fa-credit-card"></i>
          </div>
          <div class="card-content">
            <h3 class="cabin-family semi-bold">{{ formatCurrency(totalSpentAmount) }}</h3>
            <p class="secondary-text">Total Spent</p>
          </div>
        </div>

        <div class="overview-card">
          <div class="card-icon secondary">
            <i class="fas fa-chart-line"></i>
          </div>
          <div class="card-content">
            <h3 class="cabin-family semi-bold">{{ budgetProgress }}%</h3>
            <p class="secondary-text">Budget Used</p>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div v-if="activeBudgets.length > 0" class="quick-actions">
        <h3 class="cabin-family semi-bold">Quick Actions</h3>
        <div class="actions-grid">
          <button
            class="action-card"
            @click="handleQuickSpending"
          >
            <div class="action-icon primary">
              <i class="fas fa-plus"></i>
            </div>
            <div class="action-content">
              <h4 class="cabin-family semi-bold">Track Spending</h4>
              <p class="secondary-text">Add a new expense</p>
            </div>
          </button>

          <button
            class="action-card"
            @click="showCategoriesOverview = true"
          >
            <div class="action-icon success">
              <i class="fas fa-chart-pie"></i>
            </div>
            <div class="action-content">
              <h4 class="cabin-family semi-bold">View Categories</h4>
              <p class="secondary-text">See spending breakdown</p>
            </div>
          </button>

          <button
            class="action-card"
            @click="generateReport"
          >
            <div class="action-icon secondary">
              <i class="fas fa-file-alt"></i>
            </div>
            <div class="action-content">
              <h4 class="cabin-family semi-bold">Generate Report</h4>
              <p class="secondary-text">Monthly spending report</p>
            </div>
          </button>
        </div>
      </div>

      <!-- Filter and Search -->
      <div class="budgets-controls">
        <div class="search-filter">
          <div class="search-box">
            <i class="fas fa-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search budgets..."
              class="form-control"
            />
          </div>

          <div class="filter-dropdown">
            <select v-model="selectedFilter" class="form-control">
              <option value="all">All Budgets</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="over_budget">Over Budget</option>
            </select>
          </div>

          <div class="sort-dropdown">
            <select v-model="selectedSort" class="form-control">
              <option value="created_desc">Newest First</option>
              <option value="created_asc">Oldest First</option>
              <option value="name_asc">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Budgets Content -->
      <div class="budgets-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <LoadingSpinner size="large" text="Loading your budgets..." />
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <div class="error-card">
            <i class="fas fa-exclamation-triangle"></i>
            <h3 class="cabin-family semi-bold">Unable to load budgets</h3>
            <p>{{ error }}</p>
            <button class="btn primary-btn" @click="loadBudgets">
              <i class="fas fa-redo"></i>
              Try Again
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredBudgets.length === 0 && !searchQuery" class="empty-state">
          <div class="empty-card">
            <i class="fas fa-calculator"></i>
            <h3 class="cabin-family semi-bold">No budgets yet</h3>
            <p>
              Create your first budget to start tracking your spending and reach your goals.
            </p>
            <button
              class="btn primary-btn"
              @click="openCreateBudgetModal"
            >
              <i class="fas fa-plus"></i>
              Create Your First Budget
            </button>
          </div>
        </div>

        <!-- No Search Results -->
        <div v-else-if="filteredBudgets.length === 0 && searchQuery" class="no-results-state">
          <div class="empty-card">
            <i class="fas fa-search"></i>
            <h3 class="cabin-family semi-bold">No budgets found</h3>
            <p>No budgets match your search criteria. Try adjusting your filters.</p>
            <button class="btn btn-outline-secondary" @click="clearFilters">
              Clear Filters
            </button>
          </div>
        </div>

        <!-- Budgets Grid -->
        <transition-group
          v-else
          name="budgets-list"
          tag="div"
          class="budgets-grid"
        >
          <BudgetCard
            v-for="budget in filteredBudgets"
            :key="budget.id"
            :budget="budget"
            @track-spending="handleTrackSpending"
            @view-details="handleViewDetails"
          />
        </transition-group>
      </div>

      <!-- Categories Over Budget Alert -->
      <div v-if="categoriesOverBudget.length > 0" class="over-budget-alert">
        <div class="alert-header">
          <i class="fas fa-exclamation-triangle"></i>
          <h4 class="cabin-family semi-bold">Categories Over Budget</h4>
        </div>
        <div class="over-budget-list">
          <div
            v-for="category in categoriesOverBudget.slice(0, 3)"
            :key="category.name"
            class="over-budget-item"
          >
            <span class="category-name">{{ formatCategoryName(category.name) }}</span>
            <span class="over-amount primary cabin-family semi-bold">
              +{{ formatCurrency(category.spent_amount - category.amount) }}
            </span>
          </div>
        </div>
        <button v-if="categoriesOverBudget.length > 3" class="btn btn-link">
          View All ({{ categoriesOverBudget.length }})
        </button>
      </div>
    </div>

    <!-- Modals -->
    <BudgetCreationWizard
      :visible="showCreateBudgetModal"
      @update:visible="showCreateBudgetModal = $event"
      @budget-created="handleBudgetCreated"
    />

    <SpendingTracker
      :visible="showSpendingModal"
      :budget="selectedBudget"
      @update:visible="showSpendingModal = $event"
      @spending-tracked="handleSpendingTracked"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { formatCurrency } from '../utils/formatters';
import { LoadingSpinner } from '../components/shared';
import { BudgetCard, BudgetCreationWizard, SpendingTracker } from '../components/budgets';

export default {
  name: 'Budgets',
  components: {
    LoadingSpinner,
    BudgetCard,
    BudgetCreationWizard,
    SpendingTracker,
  },
  data() {
    return {
      showCreateBudgetModal: false,
      showSpendingModal: false,
      showCategoriesOverview: false,
      selectedBudget: null,
      searchQuery: '',
      selectedFilter: 'all',
      selectedSort: 'created_desc',
    };
  },
  computed: {
    ...mapState('budgets', ['budgets', 'loading', 'error']),
    ...mapGetters('budgets', [
      'activeBudgets',
      'categoriesOverBudget',
      'totalSystemBudgetAmount',
      'totalSystemSpentAmount',
    ]),

    // Use centralized system totals from store
    totalBudgetAmount() {
      return this.totalSystemBudgetAmount;
    },

    totalSpentAmount() {
      return this.totalSystemSpentAmount;
    },

    // Calculate overall budget progress across all budgets
    budgetProgress() {
      if (this.totalBudgetAmount === 0) return 0;
      return Math.round((this.totalSpentAmount / this.totalBudgetAmount) * 100);
    },

    filteredBudgets() {
      let filtered = [...this.budgets];

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((budget) => budget.name?.toLowerCase().includes(query));
      }

      // Apply status filter
      switch (this.selectedFilter) {
        case 'active':
          filtered = filtered.filter((budget) => budget.status === 'active');
          break;
        case 'inactive':
          filtered = filtered.filter((budget) => budget.status === 'inactive');
          break;
        case 'over_budget':
          filtered = filtered.filter((budget) => {
            const totalBudget = Object.values(budget?.categories || {})
              .reduce((total, cat) => total + (cat.amount || 0), 0);

            let totalSpent = 0;
            if (budget.performance && budget.performance.overall) {
              totalSpent = budget.performance.overall.totalActual || 0;
            } else if (budget.budget_trackings) {
              totalSpent = budget.budget_trackings.reduce(
                (total, tracking) => total + (tracking.actual_amount || 0), 0,
              );
            }

            return totalSpent > totalBudget;
          });
          break;
        default:
      }

      // Apply sorting
      switch (this.selectedSort) {
        case 'created_asc':
          filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          break;
        case 'created_desc':
          filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          break;
        case 'name_asc':
          filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          break;
        case 'name_desc':
          filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
          break;
        default:
      }

      return filtered;
    },
  },
  async mounted() {
    await this.loadBudgets();
  },
  methods: {
    ...mapActions('budgets', ['fetchBudgets']),

    formatCurrency,

    openCreateBudgetModal() {
      console.log('Create Budget button clicked!');
      console.log('Current showCreateBudgetModal value:', this.showCreateBudgetModal);
      this.showCreateBudgetModal = true;
      console.log('New showCreateBudgetModal value:', this.showCreateBudgetModal);
    },

    async loadBudgets() {
      await this.fetchBudgets();
    },

    getSpentColorClass() {
      if (this.budgetProgress >= 100) return 'danger';
      if (this.budgetProgress >= 80) return 'warning';
      return 'success';
    },

    formatCategoryName(name) {
      return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },

    handleQuickSpending() {
      if (this.activeBudgets.length === 1) {
        [this.selectedBudget] = this.activeBudgets;
        this.showSpendingModal = true;
      } else if (this.activeBudgets.length > 1) {
        // Show budget selection modal or use the first active budget
        [this.selectedBudget] = this.activeBudgets;
        this.showSpendingModal = true;
      }
    },

    handleTrackSpending(budget) {
      this.selectedBudget = budget;
      this.showSpendingModal = true;
    },

    handleViewDetails(budget) {
      // Navigate to budget details page
      this.$router.push(`/budgets/${budget.id}`);
    },

    handleBudgetCreated(budget) {
      this.$toast.success(`Budget "${budget.name}" created successfully!`);
    },

    async handleSpendingTracked() {
      this.$toast.success('Spending tracked successfully!');
      // Reload budgets to reflect the new spending data
      await this.loadBudgets();
    },

    generateReport() {
      // Implement report generation
      this.$toast.info('Report generation feature coming soon!');
    },

    clearFilters() {
      this.searchQuery = '';
      this.selectedFilter = 'all';
      this.selectedSort = 'created_desc';
    },

    goBack() {
      this.$router.push('/');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variable.scss';

.budgets-page {
  min-height: 100vh;
  background: #F9F9F9;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 1rem;

  .header-content h1 {
    margin-bottom: 0.5rem;
    color: $black;
  }

  .header-top {
    margin-bottom: 0.5rem;
  }

  .back-btn {
    background: transparent;
    border: 1px solid $secondary;
    color: $secondary;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;

    &:hover {
      background: $secondary;
      color: $white;
    }

    i {
      font-size: 12px;
    }
  }

  .header-actions {
    flex-shrink: 0;
  }
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.75rem;
  }
}

.overview-card {
  background: $white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(224, 224, 224, 0.5);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  }

  .card-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;

    &.primary {
      background: linear-gradient(135deg, $primary-light 0%, rgba($primary, 0.1) 100%);
      color: $primary;
    }

    &.success {
      background: linear-gradient(135deg, rgba($success, 0.2) 0%, rgba($success, 0.05) 100%);
      color: $success;
    }

    &.warning {
      background: linear-gradient(135deg, rgba(#FF9800, 0.2) 0%, rgba(#FF9800, 0.05) 100%);
      color: #FF9800;
    }

    &.danger {
      background: linear-gradient(135deg, rgba($primary, 0.2) 0%, rgba($primary, 0.05) 100%);
      color: $primary;
    }

    &.secondary {
      background: linear-gradient(135deg, rgba($secondary, 0.2) 0%, rgba($secondary, 0.05) 100%);
      color: $secondary;
    }
  }

  .card-content {
    flex: 1;
    min-width: 0;
    overflow: hidden;

    h3 {
      margin-bottom: 0.25rem;
      color: $black;
      font-size: 24px;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
      line-height: 1.2;
      max-width: 100%;
      @media (max-width: 480px) {
        font-size: 18px;
      }
      // For extremely long numbers, use even smaller font
      @media (max-width: 360px) {
        font-size: 16px;
      }
    }

    p {
      margin-bottom: 0;
      font-size: 14px;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  }
}

.quick-actions {
  margin-bottom: 2rem;

  h3 {
    margin-bottom: 1rem;
    color: $black;
  }
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.action-card {
  background: $white;
  border: 1px solid #E0E0E0;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    border-color: $primary;
  }

  .action-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;

    &.primary {
      background: $primary-light;
      color: $primary;
    }

    &.success {
      background: rgba($success, 0.2);
      color: $success;
    }

    &.secondary {
      background: rgba($secondary, 0.2);
      color: $secondary;
    }
  }

  .action-content {
    flex: 1;

    h4 {
      margin-bottom: 0.25rem;
      color: $black;
      font-size: 16px;
    }

    p {
      margin-bottom: 0;
      font-size: 12px;
    }
  }
}

.budgets-controls {
  margin-bottom: 2rem;
}

.search-filter {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;

  i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: $secondary;
    z-index: 2;
  }

  input {
    padding-left: 40px;
  }
}

.filter-dropdown,
.sort-dropdown {
  min-width: 150px;
}

.budgets-content {
  margin-bottom: 3rem;
}

.loading-state,
.error-state,
.empty-state,
.no-results-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.error-card,
.empty-card {
  background: $white;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  i {
    font-size: 24px;
    color: $secondary;
    margin-bottom: 0;
  }

  h3 {
    margin-bottom: 1rem;
    color: $black;
  }

  p {
    margin-bottom: 1.5rem;
    color: $secondary;
  }
}

.budgets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.over-budget-alert {
  background: rgba($primary, 0.1);
  border: 1px solid rgba($primary, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;

  .alert-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    i {
      color: $primary;
      font-size: 20px;
    }

    h4 {
      margin-bottom: 0;
      color: $primary;
    }
  }

  .over-budget-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .over-budget-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: $white;
    border-radius: 8px;

    .category-name {
      color: $black;
      font-weight: 500;
    }
  }

  .btn-link {
    color: $primary;
    text-decoration: none;
    font-size: 14px;
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }
}

@media (max-width: 768px) {
  .budgets-page .content {
    padding: 1rem;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;

    .header-actions {
      align-self: stretch;
    }
  }

  .overview-cards {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }

  .actions-grid {
    grid-template-columns: 1fr;
  }

  .search-filter {
    flex-direction: column;

    .search-box {
      min-width: unset;
    }

    .filter-dropdown,
    .sort-dropdown {
      min-width: unset;
    }
  }

  .budgets-grid {
    grid-template-columns: 1fr;
  }

  .over-budget-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
// Micro-animations for the grid
.budgets-list-enter-active,
.budgets-list-leave-active {
  transition: all 0.5s ease;
}
.budgets-list-enter-from,
.budgets-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
.budgets-list-move {
  transition: transform 0.5s ease;
}

.glass-morphism {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.empty-card {
  @extend .glass-morphism;
}
</style>
